import React, { useEffect, useState } from "react";
import TeamHeader from "../TeamHeader/TeamHeader";
import Chart from "react-apexcharts";
import './ServerStatus.css'
import '../TeamPortalCSS.css'

// Assets
import { HiOutlineServer } from "react-icons/hi2";
import { FaRegFloppyDisk } from "react-icons/fa6";
import { LuMemoryStick } from "react-icons/lu";
import { GrGithub } from "react-icons/gr";
import { FaInfoCircle } from "react-icons/fa";

interface ServerStatusEntry {
  status_id: number;
  server_ip: string;
  created_at: string;
  payload_json: string;
}

interface ParsedPayload {
  machine: { node_name: string; public_ip: string };
  cpu: { avg_percent: number; core_count: number };
  memory: {
    total_bytes: number;
    used_bytes: number;
    available_bytes: number;
    percent_used: number;
  },
  disk: {
    total_bytes: number;
    used_bytes: number;
    free_bytes: number;
    percent_used: number;
    ops: {
      read_ops_sec: number;
      write_ops_sec: number;
      read_bytes_sec: number;
      write_bytes_sec: number;
    };
  },
  load_average: {
    "1_min": number;
  }
  network: { bytes_sent: number; bytes_received: number };
}

const formatBytes = (bytes: number) => {
  if (bytes >= 1e12) return (bytes / 1e12).toFixed(2) + " TB";
  if (bytes >= 1e9) return (bytes / 1e9).toFixed(2) + " GB";
  if (bytes >= 1e6) return (bytes / 1e6).toFixed(2) + " MiB";
  if (bytes >= 1e3) return (bytes / 1e3).toFixed(2) + " KiB";
  return bytes + " B";
};

const ServerStatus = () => {
  const [history, setHistory] = useState<ServerStatusEntry[]>([]);
  const [openServer, setOpenServer] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split("T")[0]; // YYYY-MM-DD
  });
  const [showAddServerPopup, setShowAddServerPopup] = useState(false);


  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await fetch(
          "/api/server-status/history/",
          { credentials: "same-origin" }
        );
        const data = await response.json();
        if (response.ok) {
          setHistory(data);

          // Select first server automatically
          if (data.length > 0 && !openServer) {
            const firstIp = data[0].server_ip;
            setOpenServer(firstIp);
          }
        }
        else {
          console.error("Failed to fetch:", data.error);
        }
      } catch (error) {
        console.error("❌ Error fetching server status:", error);
      }
    };

    fetchHistory();

    // Interval refresh every 1 minute
    const interval = setInterval(fetchHistory, 60000);

    // Cleanup on unmount
    return () => clearInterval(interval);
  }, []);


  const filteredHistory = history.filter(entry => {
    const entryDay = entry.created_at.split("T")[0]; // YYYY-MM-DD
    return entryDay === selectedDate;
  });

  // If no data exists for this date
  const noData = filteredHistory.length === 0;
  const grouped: Record<string, { entries: ServerStatusEntry[]; node_name: string }> = {};

  filteredHistory.forEach((entry) => {
    let parsed: ParsedPayload | null = null;
    try {
      parsed = JSON.parse(entry.payload_json);
    } catch {
      // Bad Json
    }

    if (!parsed) return;

    const key = entry.server_ip;
    if (!grouped[key]) {
      grouped[key] = {
        entries: [],
        node_name: parsed.machine.node_name,
      };
    }
    grouped[key].entries.push(entry);
  });

  const servers = Object.keys(grouped);

  // Automatically select first server when the date changes
  useEffect(() => {
    if (servers.length === 0) {
      setOpenServer(null); // no servers available for this date
    } else if (!openServer || !servers.includes(openServer)) {
      // Only auto-select if no server is selected or the current server isn't available
      setOpenServer(servers[0]);
    }
  }, [selectedDate, servers, openServer]);


  const buildCharts = (entries: ServerStatusEntry[]) => {

    const sortedEntries = [...entries].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );


    const payloads: ParsedPayload[] = sortedEntries
      .map((e) => {
        try {
          return JSON.parse(e.payload_json) as ParsedPayload;
        } catch {
          return null;
        }
      })
      .filter(Boolean) as ParsedPayload[];

    const timestamps = sortedEntries.map((e) => {
      // created_at is already an ISO UTC string
      const time = e.created_at.split("T")[1];  // HH:MM:SS.sssZ
      return time.slice(0, 5); // HH:MM
    });

    const cpuSeries = [
      { name: "CPU Avg %", data: payloads.map((p) => p.cpu.avg_percent) },
    ];

    const cpuOptions: ApexCharts.ApexOptions = {
      chart: { type: "area", height: 250, toolbar: { show: false } },
      colors: ["#28aee1"],
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 2 },
      xaxis: {
        categories: timestamps, // all readings
        tickAmount: Math.min(timestamps.length, 8), // show max 12 ticks
        labels: {
          rotate: -45,
          showDuplicates: false, // avoids duplicate labels
        },
      },
      yaxis: { title: { text: "CPU %" } },
      tooltip: { y: { formatter: (val) => `${val.toFixed(2)} %` } },
    };

    const toGiB = (bytes: number) => bytes / 1024 / 1024 / 1024;
    const formatGiB = (val: number) => val.toFixed(2) + " GiB";
    const formatGiB2 = (val: number) => val.toFixed(1) + " GiB";

    const netSeries = [
      { name: "Bytes Sent (GiB)", data: payloads.map(p => toGiB(p.network.bytes_sent)), yAxisIndex: 0 },
      { name: "Bytes Received (GiB)", data: payloads.map(p => toGiB(p.network.bytes_received)), yAxisIndex: 1 },
    ];

    const netOptions: ApexCharts.ApexOptions = {
      chart: { type: "area", height: 250, toolbar: { show: false } },
      colors: ["#28aee1", "#16517B"],
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 2 },
      xaxis: {
        categories: timestamps, // all readings
        tickAmount: Math.min(timestamps.length, 8), // show max 12 ticks
        labels: {
          rotate: -45,
          showDuplicates: false, // avoids duplicate labels
        },
      },
      yaxis: [
        {
          title: { text: "Bytes Sent" },
          labels: { formatter: formatGiB2, style: { colors: "#28aee1" } },
        },
        {
          opposite: true,
          title: { text: "Bytes Received" },
          labels: { formatter: formatGiB2, style: { colors: "#16517B" } },
        }
      ],
      tooltip: { y: { formatter: formatGiB } },
    };



    const diskBytesSeries = [
      {
        name: "Disk Read Bytes/sec",
        data: payloads.map((p) => p.disk.ops.read_bytes_sec),
      },
      {
        name: "Disk Write Bytes/sec",
        data: payloads.map((p) => p.disk.ops.write_bytes_sec),
      },
    ];

    const diskBytesOptions: ApexCharts.ApexOptions = {
      chart: { type: "area", height: 250, toolbar: { show: false } },
      colors: ["#28aee1", "#16517B"],
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 2 },
      xaxis: {
        categories: timestamps, // all readings
        tickAmount: Math.min(timestamps.length, 8), // show max 12 ticks
        labels: {
          rotate: -45,
          showDuplicates: false, // avoids duplicate labels
        },
      },
      yaxis: [
        {
          title: { text: "Disk Read Bytes/sec" },
          labels: { formatter: formatBytes, style: { colors: "#28aee1" } },
        },
        {
          opposite: true,
          title: { text: "Disk Write Bytes/sec" },
          labels: { formatter: formatBytes, style: { colors: "#16517B" } },
        }
      ],
      tooltip: { y: { formatter: (val) => formatBytes(val) } },
    };

    const diskOpsSeries = [
      {
        name: "Disk Read Ops/sec",
        data: payloads.map((p) => p.disk.ops.read_ops_sec),
      },
      {
        name: "Disk Write Ops/sec",
        data: payloads.map((p) => p.disk.ops.write_ops_sec),
      },
    ];

    const diskOpsOptions: ApexCharts.ApexOptions = {
      chart: { type: "area", height: 250, toolbar: { show: false } },
      colors: ["#28aee1", "#16517B"],
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 2 },
      xaxis: {
        categories: timestamps, // all readings
        tickAmount: Math.min(timestamps.length, 8), // show max 12 ticks
        labels: {
          rotate: -45,
          showDuplicates: false, // avoids duplicate labels
        },
      },
      yaxis: [
        {
          title: { text: "Disk Read Ops/sec" },
          labels: { style: { colors: "#28aee1" } },
        },
        {
          opposite: true,
          title: { text: "Disk Write Ops/sec" },
          labels: { style: { colors: "#16517B" } },
        }
      ],
      tooltip: { y: { formatter: (val) => `${val.toFixed(2)} ops/sec` } },
    };

    return (
      <div className="ss-graph-wrapper">
        <div className="ss-grid">
          <div className="ss-chart">
            <div className="ss-chart-title-and-time">
              <h3>CPU Average (%)</h3>
              <p><FaInfoCircle /> UTC time</p>
            </div>
            
            <Chart options={cpuOptions} series={cpuSeries} type="area" height={250} />
          </div>

          <div className="ss-chart">
            <div className="ss-chart-title-and-time">
              <h3>Network Total</h3>
              <p><FaInfoCircle /> UTC time</p>
            </div>
            <Chart options={netOptions} series={netSeries} type="area" height={250} />
          </div>

          <div className="ss-chart">
            <div className="ss-chart-title-and-time">
              <h3>Disk Bytes</h3>
              <p><FaInfoCircle /> UTC time</p>
            </div>
            <Chart
              options={diskBytesOptions}
              series={diskBytesSeries}
              type="area"
              height={250}
            />
          </div>

          <div className="ss-chart">
            <div className="ss-chart-title-and-time">
              <h3>Disk Operations (Ops/sec)</h3>
              <p><FaInfoCircle /> UTC time</p>
            </div>
            <Chart
              options={diskOpsOptions}
              series={diskOpsSeries}
              type="area"
              height={250}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="customer-directory-body">
      <TeamHeader title="Server" />

      <div className="server-machines-and-info">

        <div className="server-machines-section">

          <div>
            <h1>Servers</h1>
            <div className="server-items">
              <p style={{ margin: 0 }}>
                <strong>Active</strong>{" "}
                <span className="server-count-circle">{servers.length}</span>
              </p>

              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                style={{
                  padding: "0.2rem 0.5rem",
                  borderRadius: "6px",
                  border: "1px solid #ccc"
                }}
              />
            </div>


            <div className="server-machines">
              {servers.map((ip) => {
                const server = grouped[ip];
                const isSelected = openServer === ip;

                // parse one of the payloads to get machine.machine
                let machineType = "";
                try {
                  const latestPayload = JSON.parse(server.entries[0].payload_json) as ParsedPayload;
                  machineType = latestPayload.machine.public_ip;
                } catch {
                  machineType = "Unknown";
                }

                return (
                  <div
                    key={ip}
                    className={`server-machine ${isSelected ? "active" : ""}`}
                    onClick={() => setOpenServer(ip)}
                  >
                    <div>
                      <strong style={{ fontWeight: '600' }}>{server.node_name}</strong> {/* Node name */}
                      <p>{machineType}</p> {/* IP and machine type */}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <button
            className="general-button"
            style={{ margin: '0rem 1rem' }}
            onClick={() => {
              setShowAddServerPopup(true);
            }}>
            + Add New Server
          </button>


        </div>

        <div className="server-slips-wrapper">


          {noData && (
            <div style={{ padding: "2rem", textAlign: "center" }}>
              <h2>No server data found for {selectedDate}</h2>
            </div>
          )}

          {!noData && openServer && (
            (() => {
              const server = grouped[openServer];
              if (!server || !server.entries.length) return null;

              const latestEntry = server.entries[0];

              let latestPayload: ParsedPayload | null = null;
              try {
                latestPayload = JSON.parse(latestEntry.payload_json);
              } catch {
                //Bad json 
              }

              // MEMORY
              const memUsed = latestPayload?.memory.used_bytes ?? 0;
              const memTotal = latestPayload?.memory.total_bytes ?? 1;
              const memPercent = latestPayload?.memory.percent_used ?? 0;

              // DISK
              const diskUsed = latestPayload?.disk.used_bytes ?? 0;
              const diskTotal = latestPayload?.disk.total_bytes ?? 1;
              const diskPercent = latestPayload?.disk.percent_used ?? 0;

              // LOAD
              const load1 = latestPayload?.load_average["1_min"] ?? 0;
              const cpuCount = latestPayload?.cpu.core_count ?? 1;
              // CPU
              const cpuAvgPercent = latestPayload?.cpu.avg_percent ?? 0;
              const cpuCores = latestPayload?.cpu.core_count ?? 1;


              return (
                <div className="server-slip-body">

                  <div className="server-stats-cards">

                    {/* MEMORY */}
                    <div className="server-stat-card">
                      <div className="server-stat">
                        <LuMemoryStick className="server-stat-icon" />
                        <div>
                          <strong>Memory</strong>
                          <p>Total: {formatBytes(memTotal)} </p>
                        </div>
                      </div>

                      <div className="server-stat2">
                        <p>
                          Used: {formatBytes(memUsed)} | {memPercent.toFixed(1)}%
                        </p>

                        <div className="stat-bar">
                          <div
                            className="stat-bar-fill"
                            style={{ width: `${memPercent}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    {/* DISK */}
                    <div className="server-stat-card">
                      <div className="server-stat">
                        <div className="server-stat-icon">
                          <FaRegFloppyDisk className="disk-icon" />
                        </div>

                        <div>
                          <strong>Disk</strong>
                          <p>Total: {formatBytes(diskTotal)}</p>
                        </div>
                      </div>

                      <div className="server-stat2">
                        <p>
                          Used: {formatBytes(diskUsed)} | {diskPercent.toFixed(1)}%
                        </p>

                        <div className="stat-bar">
                          <div
                            className="stat-bar-fill disk"
                            style={{ width: `${diskPercent}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    {/* CPU % */}
                    <div className="server-stat-card">
                      <div className="server-stat">
                        <HiOutlineServer className="server-stat-icon" />
                        <div>
                          <strong>CPU Usage</strong>
                          <p>{cpuAvgPercent.toFixed(1)}% | {cpuCores} cores</p>
                        </div>
                      </div>

                      <div className="server-stat2">
                        <p>{cpuAvgPercent.toFixed(1)}%</p>

                        <div className="stat-bar">
                          <div
                            className="stat-bar-fill load"
                            style={{ width: `${cpuAvgPercent}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>


                  </div>

                  {!noData && openServer && grouped[openServer]?.entries?.length > 0 && (
                    buildCharts(grouped[openServer].entries)
                  )}

                </div>
              );
            })()
          )}
        </div>

      </div>

      {/* Add Server Popup */}
      {showAddServerPopup && (
        <div className='add-item-popup-overlay'>
          <div className='add-item-popup-content hh-del-pop'>
            <div className='hh-delete-inner-pop' style={{ gap: '2rem' }}>
              <div className='hh-delete-text-and-icon'>

                <GrGithub className="ss-git-icon" />

                <div>
                  <h3>Instructions to Add Your Machine</h3>
                  <p>
                    Here you can find the necessary scripts and step-by-step instructions
                    on how to send your server&apos;s information to this page.
                  </p><br />
                  <a
                    href="https://github.com/fire-neural-network/server-monitoring-django"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="general-link"
                  >
                    Server Monitoring Repository
                  </a>
                </div>

              </div>

              <div className="hh-duo hh-actions">
                <button
                  className="cancel-btn"
                  onClick={() => {
                    setShowAddServerPopup(false);
                  }}
                >
                  Close
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ServerStatus;
